// From the type module rather than the `@data` barrel, for the same reason `UnreadCommentDot` is: this
// file is imported by a test that pulls in nothing else, and the barrel drags a cycle in behind it.
import { SprintProductStatus } from '@data/utils/types';
import { hasDispute, isAnsweringRemark } from './approvalSummary/UnreadCommentDot';

/** Everything a member can do to a report, whichever surface they are looking at it from. */
export enum SprintAction {
  close = 'close',
  /** Closing a report we reopened to answer a remark — it reads differently, and it mails the client. */
  closeAnswer = 'closeAnswer',
  reopen = 'reopen',
  dispute = 'dispute',
  approve = 'approve',
  dismissDispute = 'dismissDispute',
  send = 'send',
  invoice = 'invoice',
}

/** The report fields the rule reads. A row from a list satisfies this as well as a hydrated report does. */
export interface DecidableReport {
  status?: SprintProductStatus;
  disputedAt?: Date | null;
  comment?: string;
  disputeResolvedAt?: Date | null;
}

/**
 * What a report offers, in the order it should be offered.
 *
 * One rule, because three surfaces show these actions — the report's own header, the products reporting
 * list, and a product's Sprints tab — and they each had their own copy. They disagreed: a disputed report
 * offered Approve on a product's Sprints tab, which answers the client's remark on their behalf and
 * settles it, while the report itself deliberately refuses that and offers Dismiss dispute instead. A
 * surface may still choose to render fewer of these (a compact row can drop the trailing ones), but it may
 * not offer something this rule does not.
 */
export const sprintActionsFor = (report?: DecidableReport): SprintAction[] => {
  switch (report?.status) {
    case SprintProductStatus.approved:
      return [SprintAction.invoice, SprintAction.send];
    case SprintProductStatus.invoiced:
      return [SprintAction.send];
    case SprintProductStatus.closed:
      // A disputed report is waiting on us to answer the remark, so those are the two things on offer.
      // Approving over the top of it is not one of them, and neither is a second dispute.
      return hasDispute(report)
        ? [SprintAction.dismissDispute, SprintAction.reopen, SprintAction.send]
        : [SprintAction.reopen, SprintAction.dispute, SprintAction.approve, SprintAction.send];
    default:
      // Open, or no report filed for this sprint yet. While a remark is on the desk, closing is the answer
      // to it, so the button says which answer it will be.
      return [isAnsweringRemark(report) ? SprintAction.closeAnswer : SprintAction.close];
  }
};

/** Whether this report offers the action — for a surface rendering one button rather than the set. */
export const offersSprintAction = (report: DecidableReport | undefined, action: SprintAction): boolean =>
  sprintActionsFor(report).includes(action);

/** The one a phone shows as a button, the rest going under "more": the step the report is waiting on. */
// A record, so a new action cannot compile until it is given its place here.
const MAIN_ACTION_RANK: Record<SprintAction, number> = {
  [SprintAction.close]: 0,
  [SprintAction.closeAnswer]: 1,
  [SprintAction.approve]: 2,
  [SprintAction.dismissDispute]: 3,
  [SprintAction.invoice]: 4,
  [SprintAction.send]: 5,
  [SprintAction.reopen]: 6,
  [SprintAction.dispute]: 7,
};

export const mainSprintAction = (actions: SprintAction[]): SprintAction | undefined =>
  [...actions].sort((a, b) => MAIN_ACTION_RANK[a] - MAIN_ACTION_RANK[b])[0];
